import React, { useState, useEffect, useMemo } from 'react';
import { Search, SortAsc, SortDesc, Download, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { useAuth } from '../../auth/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const ParentsList = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const { t } = useTranslation('parentlist');
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // States for filtering and sorting
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedParents, setSelectedParents] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const pageSize = 10;

  useEffect(() => {
    const fetchParents = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/v1/admin/schools/${user.school_reg}/parents`);
        setParents(response.data.data);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchParents();
  }, [api, user.school_reg]);

  useEffect(() => {
    // Reset selections when page changes
    setSelectedParents([]);
    setSelectAll(false);
  }, [currentPage]);

  const handleRowClick = (parentId, e) => {
    // Prevent navigation if the click was on a checkbox
    if (e.target.type === 'checkbox' || e.target.closest('.checkbox-cell')) {
      return;
    }
    navigate(`/parent-profile/${parentId}`);
  };

  const handleCheckboxChange = (parentId) => {
    setSelectedParents(prev => {
      if (prev.includes(parentId)) {
        return prev.filter(id => id !== parentId);
      } else {
        return [...prev, parentId];
      }
    });
  };

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedParents([]);
    } else {
      setSelectedParents(paginatedParents.map(parent => parent.id));
    }
    setSelectAll(!selectAll);
  };

  const handleDeleteSelected = async () => {
    if (selectedParents.length === 0) return;
    
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      // Use Promise.all to handle multiple delete requests concurrently
      await Promise.all(
        selectedParents.map(parentId => 
          api.delete(`/api/v1/admin/schools/${user.school_reg}/parents/${parentId}`)
        )
      );
      
      // Remove deleted parents from state
      setParents(prev => prev.filter(parent => !selectedParents.includes(parent.id)));
      setSelectedParents([]);
      setSelectAll(false);
      setShowDeleteConfirm(false);
    } catch (err) {
      setError(`${t('error')}${err.message}`);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const getRandomColor = (name) => {
    const colors = [
      '#2563eb', '#7c3aed', '#db2777', '#dc2626', '#059669',
      '#ca8a04', '#0891b2', '#4f46e5', '#be123c', '#15803d'
    ];
    const index = name.split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  // Filter and sort parents
  const filteredAndSortedParents = useMemo(() => {
    return parents
      .filter(parent => 
        parent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        parent.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (parent.id_number && parent.id_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (parent.phone && parent.phone.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (parent.relation_type && parent.relation_type.toLowerCase().includes(searchTerm.toLowerCase()))
      )
      .sort((a, b) => {
        if (sortDirection === 'asc') {
          return a[sortField] > b[sortField] ? 1 : -1;
        }
        return a[sortField] < b[sortField] ? 1 : -1;
      });
  }, [parents, searchTerm, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedParents.length / pageSize);
  const paginatedParents = filteredAndSortedParents.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const exportToCSV = () => {
    const headers = [
      t('columns.name'), 
      t('columns.email'), 
      t('columns.phone'), 
      t('columns.idNumber'), 
      t('columns.relationType'), 
      t('columns.address')
    ];
    const csvContent = [
      headers.join(','),
      ...filteredAndSortedParents.map(parent => 
        [
          parent.name,
          parent.email,
          parent.phone,
          parent.id_number,
          parent.relation_type,
          parent.address || '-'
        ].join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'parents.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const renderSortIcon = (field) => {
    if (sortField === field) {
      return sortDirection === 'asc' ? 
        <SortAsc size={16} className="inline ml-1" /> : 
        <SortDesc size={16} className="inline ml-1" />;
    }
    return null;
  };

  const sortableHeaderClasses = "bg-slate-50 p-3 text-left font-semibold text-slate-600 border-b-2 border-slate-200 cursor-pointer select-none whitespace-nowrap hover:bg-slate-100";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <span className="ml-3">{t('loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {t('error')}{error}
      </div>
    );
  }

  const getRelationTypeDisplay = (relationType) => {
    if (relationType === 'Father') return t('relationTypes.father');
    if (relationType === 'Mother') return t('relationTypes.mother');
    return t('relationTypes.guardian');
  };

  return (
    <div className="rounded-xl">
      <div className="flex justify-between items-center mb-6 gap-4 flex-wrap">
        <div className="flex items-center bg-white border border-slate-200 rounded-lg px-4 py-2 w-full sm:w-auto sm:min-w-[300px]">
          <Search className="text-slate-500" size={20} />
          <input
            type="text"
            placeholder={t('search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border-none outline-none ml-2 text-sm w-full"
          />
        </div>

        <div className="flex flex-wrap gap-2 sm:gap-4 ml-auto">
          {selectedParents.length > 0 && (
            <button 
              className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors duration-200"
              onClick={handleDeleteSelected}
            >
              <Trash2 size={20} />
              {t('delete')} ({selectedParents.length})
            </button>
          )}
          
          <button 
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors duration-200"
            onClick={exportToCSV}
          >
            <Download size={20} />
            {t('exportCSV')}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto bg-white rounded-lg border border-slate-200 shadow-sm">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="bg-slate-50 p-3 border-b-2 border-slate-200 whitespace-nowrap w-8">
                <div className="flex items-center justify-center checkbox-cell">
                  <input 
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAllChange}
                    className="form-checkbox w-4 h-4 text-blue-500 rounded border-slate-300 cursor-pointer"
                  />
                </div>
              </th>
              <th className="bg-slate-50 p-3 text-left font-semibold text-slate-600 border-b-2 border-slate-200 whitespace-nowrap">{t('columns.photo')}</th>
              <th 
                className={sortableHeaderClasses}
                onClick={() => {
                  setSortField('name');
                  setSortDirection(sortField === 'name' ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc');
                }}
              >
                {t('columns.name')} {renderSortIcon('name')}
              </th>
              <th 
                className={sortableHeaderClasses}
                onClick={() => {
                  setSortField('id_number');
                  setSortDirection(sortField === 'id_number' ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc');
                }}
              >
                {t('columns.idNumber')} {renderSortIcon('id_number')}
              </th>
              <th className="bg-slate-50 p-3 text-left font-semibold text-slate-600 border-b-2 border-slate-200 whitespace-nowrap">{t('columns.email')}</th>
              <th className="bg-slate-50 p-3 text-left font-semibold text-slate-600 border-b-2 border-slate-200 whitespace-nowrap">{t('columns.phone')}</th>
              <th 
                className={sortableHeaderClasses}
                onClick={() => {
                  setSortField('relation_type');
                  setSortDirection(sortField === 'relation_type' ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc');
                }}
              >
                {t('columns.relationType')} {renderSortIcon('relation_type')}
              </th>
              <th className="bg-slate-50 p-3 text-left font-semibold text-slate-600 border-b-2 border-slate-200 whitespace-nowrap">{t('columns.address')}</th>
            </tr>
          </thead>
          <tbody>
            {paginatedParents.map(parent => (
              <tr 
                key={parent.id}
                onClick={(e) => handleRowClick(parent.id, e)}
                className={`cursor-pointer hover:bg-slate-50 ${
                  selectedParents.includes(parent.id) ? 'bg-blue-50' : ''
                }`}
              >
                <td className="p-3 border-b border-slate-200 w-8">
                  <div 
                    className="flex items-center justify-center checkbox-cell"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input 
                      type="checkbox"
                      checked={selectedParents.includes(parent.id)}
                      onChange={() => handleCheckboxChange(parent.id)}
                      className="form-checkbox w-4 h-4 text-blue-500 rounded border-slate-300 cursor-pointer"
                    />
                  </div>
                </td>
                <td className="p-3 border-b border-slate-200">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-white uppercase"
                    style={{ backgroundColor: getRandomColor(parent.name) }}
                  >
                    {parent.name.charAt(0)}
                  </div>
                </td>
                <td className="p-3 border-b border-slate-200 text-slate-700">{parent.name}</td>
                <td className="p-3 border-b border-slate-200 text-slate-700">{parent.id_number}</td>
                <td className="p-3 border-b border-slate-200 text-slate-700">{parent.email}</td>
                <td className="p-3 border-b border-slate-200 text-slate-700">{parent.phone}</td>
                <td className="p-3 border-b border-slate-200">
                  <span className={`px-2 py-1 rounded-xl text-xs font-medium ${
                    parent.relation_type === 'Father' 
                      ? 'bg-blue-100 text-blue-800' 
                      : parent.relation_type === 'Mother'
                        ? 'bg-pink-100 text-pink-800'
                        : 'bg-purple-100 text-purple-800'
                  }`}>
                    {getRelationTypeDisplay(parent.relation_type)}
                  </span>
                </td>
                <td className="p-3 border-b border-slate-200 text-slate-700">{parent.address || '-'}</td>
              </tr>
            ))}
            {paginatedParents.length === 0 && (
              <tr>
                <td colSpan="8" className="p-6 text-center text-slate-500">
                  {t('noParents')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Responsive pagination controls */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
        <div className="text-sm text-slate-500 order-2 sm:order-1">
          {t('totalParents')}{filteredAndSortedParents.length}
        </div>
        
        <div className="flex items-center justify-center gap-4 order-1 sm:order-2">
          <button 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="p-2 border border-slate-200 bg-white rounded-lg cursor-pointer flex items-center transition-all duration-200 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={20} />
          </button>
          <span className="text-slate-600 text-sm">
            {t('page')} {currentPage} {t('of')} {totalPages || 1}
          </span>
          <button 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages || totalPages === 0}
            className="p-2 border border-slate-200 bg-white rounded-lg cursor-pointer flex items-center transition-all duration-200 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">{t('confirmDeleteTitle')}</h3>
            <p className="mb-6">
              {t('confirmDeleteMessage', { count: selectedParents.length })}
            </p>
            <div className="flex justify-end gap-3">
              <button 
                onClick={cancelDelete}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
              >
                {t('cancel')}
              </button>
              <button 
                // onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                {t('delete_action')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentsList;